import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../../../models/User';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Textarea } from '../../../../components/Textarea';
import { Select } from '../../../../components/Select';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { useRoles } from '../../../../hooks/useRoles';
import Loading from '../../../../components/Loading';
import { useStockAdjust } from '../../../../hooks/useStockAdjust';
import { useSetupUsergroup } from '../../../../hooks/useSetupUsergroup';
import useUserService from '../../../../hooks/useUserService';
import { UserContext } from '../../../../context/userContext';
import { useWarehouses } from '../../../../hooks/useWarehouse';
import { StockAdjust } from '../../../../models/StockAdjust';

// Utility function to format date to yyyy-MM-dd based on local time
const formatDate = (isoString: string): string => {
    const date = new Date(isoString);

    // Get the year, month, and day using local time
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
    const day = date.getDate().toString().padStart(2, '0'); // Ensure two digits for day

    return `${year}-${month}-${day}`; // Format as yyyy-MM-dd
};

const EditStockAdjustPage = () => {
    const navigate = useNavigate();
    const id = useParams<{ id: string }>();
    const [stockAdjust, setStockAdjust] = useState<StockAdjust | null>(null);
    const { updateStockAdjust, fetchStockAdjustById, loading, error } = useStockAdjust();
    const { customers, logistics } = useSetupUsergroup();
    const { users } = useUserService();
    const { currentUser } = useContext(UserContext);
    const { warehouses } = useWarehouses();
    const filteredWarehouses = warehouses.filter(warehouse => warehouse.hq !== '1');

    useEffect(() => {
        document.title = "Edit Stock Adjust";
    }, []);

    useEffect(() => {
        const fetchedStockAdjustDetails = async () => {
            try {
                const fetchedStockAdjust = await fetchStockAdjustById(Number(id.id));
                if (fetchedStockAdjust!.date) {
                    fetchedStockAdjust!.date = formatDate(fetchedStockAdjust!.date);
                }
                setStockAdjust(fetchedStockAdjust);
            } catch (err) {
                showErrorToast(`Error fetching stock adjust ${id}: ${err}`);
            }
        }
        fetchedStockAdjustDetails();
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setStockAdjust(prev => prev ? ({ ...prev, [name]: value }) : null);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!stockAdjust) {
            return;
        }
        if (!stockAdjust.customer_id || !stockAdjust.date || !stockAdjust.issuer_id || !stockAdjust.logistic_id || !stockAdjust.manual_no || !stockAdjust.manual_no || !stockAdjust.reference_no || !stockAdjust.warehouse_id) {
            showErrorToast("Please fill in all field before submit");
            return;
        }
        try {
            const res = await updateStockAdjust(Number(id.id), stockAdjust!);
            showLoadingToast(`Updating ${stockAdjust!.reference_no} ...`, 100);
            if (res.dbsuccess) {
                setTimeout(() => {
                    navigate('/gp/stocks/stockadjust');
                    showSuccessToast(`${stockAdjust!.reference_no} updated successfully`);
                }, 1000);
            } else {
                showErrorToast(`Failed updating stock-adjust data`);
            }
        } catch (err) {
            showErrorToast(`Failed updating stock-adjust data: ${err}`);
        }
    };


    if (loading) return <p>Loading...</p>;
    if (error) return <p>{error}</p>;
    if (!stockAdjust) return <p>Stock adjust data not found.</p>;

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Add Stock Adjust Data</Heading>
                <div className='mt-5 flex lg:ml-4 lg:mt-0'>
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className='ml-3' disabled={(!stockAdjust.customer_id || !stockAdjust.date || !stockAdjust.issuer_id || !stockAdjust.logistic_id || !stockAdjust.manual_no || !stockAdjust.manual_no || !stockAdjust.reference_no || !stockAdjust.warehouse_id)}>Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Stock Adjust Information</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Information on inventory adjustments.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Date</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        type='date'
                                        aria-label="Date"
                                        name="date"
                                        value={stockAdjust.date!}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Warehouse</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Warehouse"
                                        name="warehouse_id"
                                        value={stockAdjust.warehouse_id}
                                        onChange={handleChange}
                                    >
                                        {filteredWarehouses.map(warehouse => (
                                            <option key={warehouse.id.toString()} value={warehouse.id.toString()}>
                                                {warehouse.warehouse_name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Reference No</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Reference No"
                                        name="reference_no"
                                        value={stockAdjust.reference_no!}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Manual Number</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Manual Number"
                                        name="manual_no"
                                        value={stockAdjust.manual_no!}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Customer</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Customer"
                                        name="customer_id"
                                        value={stockAdjust.customer_id!.toString()}
                                        onChange={handleChange}
                                    >
                                        {customers.map(customer => (
                                            <option key={customer.id} value={customer.id!.toString()}>
                                                {customer.fullname}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>



                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Logistic</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Logistic"
                                        name="logistic_id"
                                        value={stockAdjust.logistic_id!.toString()}
                                        onChange={handleChange}
                                    >
                                        {logistics.map(logistic => (
                                            <option key={logistic.id} value={logistic.id!.toString()}>
                                                {logistic.fullname}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Issued by</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Issuer Id"
                                        name="issuer_id"
                                        value={stockAdjust.issuer_id!.toString()}
                                        onChange={handleChange}
                                    >
                                        {users.map(user => (
                                            <option key={user.id} value={user.id!.toString()}>
                                                {user.fullname}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </Card>
            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit" disabled={(!stockAdjust.customer_id || !stockAdjust.date || !stockAdjust.issuer_id || !stockAdjust.logistic_id || !stockAdjust.manual_no || !stockAdjust.manual_no || !stockAdjust.reference_no || !stockAdjust.warehouse_id)}>Save</Button>
            </div>
        </form>
    );
};

export default EditStockAdjustPage;
